/**
 * VendFlow design tokens — "Lumina"
 *
 * Soft depth and gentle gradients: frosted glass panels, layered shadows and a
 * dual accent palette. Orange (#FF6B00) is the brand accent, used sparingly for
 * primary CTAs and key data highlights; indigo (#4F46E5) carries everything
 * interactive-but-secondary — links, focus rings, active navigation.
 *
 * Colour is delivered through CSS custom properties (defined in `index.css`)
 * so that the entire semantic vocabulary — `bg-surface`, `text-ink`,
 * `border-rule` — flips automatically when the `dark` class is set on the
 * document root. `tailwind.config.ts` consumes the variable names; runtime
 * code (charts, editors) consumes the static values exported here.
 */

// ─── Brand + static colour ───────────────────────────────────────────────────

export const colors = {
  /** Orange accent — primary CTAs, badges, key indicators. Same in both modes. */
  primary: '#FF6B00',
  primaryHover: '#E86200',
  /** Indigo — links, secondary interactive, focus rings, active nav. */
  secondary: '#4F46E5',
  secondaryHover: '#4338CA',

  /**
   * Mode-aware values, expressed as CSS variables. Safe anywhere the value
   * lands in real CSS (inline `style`, styled attributes); charts use the
   * static duals below instead, because SVG presentation attributes do not
   * resolve `var()`.
   */
  background: 'rgb(var(--vf-bg))',
  surface: 'rgb(var(--vf-surface))',
  rule: 'rgb(var(--vf-border))',
  text: {
    primary: 'rgb(var(--vf-tx1))',
    secondary: 'rgb(var(--vf-tx2))',
    tertiary: 'rgb(var(--vf-tx3))',
    onPrimary: '#FFFFFF',
  },
  signal: {
    positive: 'rgb(var(--vf-success))',
    negative: 'rgb(var(--vf-error))',
  },
} as const;

/**
 * Chart palette — primary series indigo, secondary orange, then purple, cyan,
 * emerald. Static values chosen to hold up on both the light and dark grounds,
 * because Recharts writes them into SVG attributes where `var()` cannot reach.
 */
export const chartPalette = ['#4F46E5', '#FF6B00', '#8B5CF6', '#06B6D4', '#10B981'] as const;

/** Neutral chart chrome that reads on both modes. */
export const chartChrome = {
  grid: 'rgba(148, 163, 184, 0.25)',
  axis: '#94A3B8',
  /** Tooltips are dark-surfaced in both modes, per the direction. */
  tooltipBg: '#1E293B',
  tooltipText: '#F9FAFB',
} as const;

/**
 * Email content colours. Emails keep their own conservative branding — these
 * are literal hexes because the markup leaves the platform and must not
 * reference our stylesheet.
 */
export const emailColors = {
  ink: '#191C21',
  secondary: '#5B6270',
  muted: '#8B93A1',
  orange: '#FF6B00',
  positive: '#3D7A56',
  negative: '#B23A2E',
} as const;

// ─── Typography ──────────────────────────────────────────────────────────────

export const fonts = {
  /** The platform typeface — body, headings, labels, navigation, charts. */
  sans: "'Poppins', sans-serif",
  /** Serial numbers, references, prices in tables — anything read digit by digit. */
  mono: "'JetBrains Mono', 'Fira Code', ui-monospace, SFMono-Regular, Menlo, monospace",
} as const;

export const typography = {
  heading1: { size: '28px', lineHeight: '34px', weight: 600, letterSpacing: '-0.02em' },
  heading2: { size: '22px', lineHeight: '28px', weight: 600, letterSpacing: '-0.01em' },
  heading3: { size: '17px', lineHeight: '24px', weight: 600, letterSpacing: '0em' },
  body: { size: '14px', lineHeight: '21px', weight: 400, letterSpacing: '0em' },
  bodySmall: { size: '13px', lineHeight: '18px', weight: 400, letterSpacing: '0em' },
  /** Uppercase micro-labels: group headers, table headers, KPI labels. */
  caption: {
    size: '11px',
    lineHeight: '14px',
    weight: 500,
    letterSpacing: '0.04em',
    textTransform: 'uppercase' as const,
  },
  label: { size: '13px', lineHeight: '18px', weight: 500, letterSpacing: '0em' },
  /** KPI dashboard numbers — large and light. */
  metric: { size: '32px', lineHeight: '38px', weight: 300, letterSpacing: '-0.02em' },
  metricSmall: { size: '24px', lineHeight: '30px', weight: 300, letterSpacing: '-0.01em' },
} as const;

// ─── Space, radius, elevation, layout ────────────────────────────────────────

export const spacing = {
  xs: '4px',
  sm: '8px',
  md: '12px',
  lg: '16px',
  xl: '24px',
  '2xl': '32px',
  '3xl': '48px',
  page: '32px',
  pageSm: '20px',
  section: '24px',
  row: '12px',
} as const;

export const radii = {
  none: '0px',
  sm: '6px',
  md: '8px',
  lg: '12px',
  xl: '16px',
  full: '9999px',
} as const;

/** Layered soft shadows; the dark values are defined in CSS variables. */
export const shadows = {
  sm: 'var(--vf-shadow-sm)',
  md: 'var(--vf-shadow-md)',
  lg: 'var(--vf-shadow-lg)',
} as const;

export const layout = {
  sidebarWidth: '248px',
  contentMaxWidth: '1440px',
  topBarHeight: '56px',
} as const;

export const breakpoints = {
  sm: '480px',
  md: '768px',
  lg: '1024px',
  xl: '1280px',
} as const;

export const motion = {
  fast: '120ms',
  base: '180ms',
  easing: 'cubic-bezier(0.2, 0, 0.2, 1)',
} as const;

// ─── Status → tone mapping ───────────────────────────────────────────────────

/**
 * Badge tones. Every status renders as a tinted pill:
 *   neutral  — grey (draft, archived, closed)
 *   info     — indigo (in-flight: sent, shipped, scheduled, active)
 *   positive — green (accepted, delivered, available, paid)
 *   warning  — amber (extended, expiring)
 *   negative — red (declined, cancelled, returned)
 *   critical — red tint with the orange key-indicator dot (failed QC, overdue,
 *              sync error) — the states that demand attention today.
 *   accent   — orange (on trial: stock out in the field earning its keep)
 */
export type StatusTone =
  | 'neutral'
  | 'info'
  | 'positive'
  | 'warning'
  | 'negative'
  | 'critical'
  | 'accent';

export const statusTone: Record<string, StatusTone> = {
  // Generic
  DRAFT: 'neutral',
  ACTIVE: 'info',
  INACTIVE: 'neutral',
  ARCHIVED: 'neutral',

  // Purchase orders
  SENT: 'info',
  ACKNOWLEDGED: 'info',
  SHIPPED: 'info',
  IN_TRANSIT: 'info',
  AT_PORT: 'info',
  CUSTOMS_CLEARANCE: 'info',
  LANDED: 'positive',
  RECEIVED_INTO_STOCK: 'positive',
  CANCELLED: 'negative',
  DELIVERED_TO_WAREHOUSE: 'positive',
  /// A shipment whose purchase order has been deleted. Not an error — it needs
  /// somebody to look at it, which is what warning is for.
  ORPHANED: 'warning',

  // Machines
  IN_QC: 'info',
  AVAILABLE: 'positive',
  RESERVED: 'info',
  ON_TRIAL: 'accent',
  SOLD: 'positive',
  IN_REPROCESSING: 'negative',
  /// Held at goods receipt, damaged, until somebody decides (FLOW-004).
  RECEIVED: 'warning',
  RETURNED_TO_SUPPLIER: 'neutral',
  WRITTEN_OFF: 'negative',

  // QC
  PENDING: 'neutral',
  IN_PROGRESS: 'info',
  PASSED: 'positive',
  FAILED: 'critical',

  // Quotes
  ACCEPTED: 'positive',
  CONVERTED: 'positive',
  CONVERTED_TO_ORDER: 'positive',
  SUPERSEDED: 'neutral',
  EXPIRED: 'negative',
  DECLINED: 'negative',

  // Orders / delivery
  CONFIRMED: 'positive',
  IN_PREPARATION: 'info',
  READY_FOR_DISPATCH: 'info',
  DISPATCHED: 'info',
  DELIVERED: 'positive',
  SCHEDULED: 'info',
  COLLECTED: 'info',
  REQUESTED: 'warning',
  RESCHEDULED: 'warning',

  // Trials
  EXTENDED: 'warning',
  CONVERTED_TO_SALE: 'positive',
  RETURNED: 'negative',
  OVERDUE: 'critical',

  // Claims
  OPEN: 'info',
  RESOLVED: 'positive',
  CLOSED: 'neutral',

  // E-signature
  CREATED: 'neutral',
  VIEWED: 'info',
  SIGNED: 'positive',
  COMPLETED: 'positive',

  // Sync
  SYNCED: 'positive',
  ERROR: 'critical',
  PAID: 'positive',

  // Reservations
  ON_ORDER: 'info',
  IN_STOCK: 'positive',
  RELEASED: 'negative',
};

export function toneFor(status: string | null | undefined): StatusTone {
  if (!status) return 'neutral';
  return statusTone[status.toUpperCase().replace(/[\s-]/g, '_')] ?? 'neutral';
}

export const tokens = {
  colors,
  chartPalette,
  chartChrome,
  emailColors,
  fonts,
  typography,
  spacing,
  radii,
  shadows,
  layout,
  breakpoints,
  motion,
  statusTone,
} as const;

export default tokens;
